import React from 'react';
import { useSelector } from 'react-redux';

import formatNumber from 'helpers/formatNumber';
import playlistCustomers from 'maps/customer-name-id';

import Box from 'components/elements/Box';
import CustomerIcon from 'components/base/CustomerIcon/CustomerIcon';
import { StyledCaret } from 'components/Charts/ChartTable/chartTable.style';
import CountryFlag from 'components/base/CountryFlag/index';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';

import PlaylistExpandedRow, {
  IPlaylistGraphProps,
} from './PlaylistDesktopExpandedRow';
import { ICustomer } from 'redux/reducers/customers';
import { RootState } from 'redux/rootReducer';
import { ITableHeader } from '../util/getMenuItems';

import {
  CellContent,
  EntityMeta,
  EntityMetaData,
  EntityTitle,
  TableCell,
  PlaylistTableRow as TableRow,
  DSPEntityMeta,
  ExpandedTableRow,
} from '../styles/PlaylistTableRow.style';
import { ICountryFlagProps } from 'types/shared';

const getFormatMetric = (metric: string): 'number' | 'trend' | 'percentage' => {
  switch (metric) {
    case 'skiprate_this_week_to_date':
      return 'percentage';
    case 'stream_wow_in_progress':
      return 'trend';
    default:
      return 'number';
  }
};

export const noop = (): void => {}; // eslint-disable-line @typescript-eslint/no-empty-function

export interface IPlaylistItem {
  [key: string]: any;
  countryId?: string;
  countryName?: string;
  customerId: string | number;
  displayName: string;
  followers: number;
  ownerDisplayName: string;
  position: number;
  previousPosition: number;
  skiprate_this_week_to_date: number;
  stream_count_this_week_to_date: number;
  stream_count_last_week_to_date: number;
  stream_wow_in_progress: number;
  track_count: number;
  entity_track_count: number;
  playlistId: number;
  dateAdded: Date | null;
  lastPeakDate: Date | null;
  peak: number;
  playlistTerritory?: ICountryFlagProps;
  totalDays: number;
  totalTrackCount?: number;
  newTrackCount?: number;
}

interface IPlaylistTableRowProps extends IPlaylistGraphProps {
  /** The playlist metadata which is displayed in this PlaylistTableRow */
  allPeriodKeys: Date[] | number[];
  playlist: IPlaylistItem;
  rest?: any;
  expandedId: number | null;
  expandedCountryId?: string;
  onPeriodChange: (prop?: any) => any;
  onTempPeriodChange: (prop?: any) => any;
  selectedEntity: any;
  selectedTempEntity: any;
  toggleExpand: (id: number, displayName: string, countryId?: string) => any;
  isTopList?: boolean;
  columns?: ITableHeader[];
  setPlaylistEditing: any;
  canNotAnnotate: boolean;
}

/** A single row in the PlaylistTable */
const PlaylistTableRow: React.FC<IPlaylistTableRowProps> = (props) => {
  const {
    allPeriodKeys,
    color,
    entityId,
    expandedId,
    expandedCountryId,
    fulfilled,
    hasNoData,
    isSmallScreen,
    onPeriodChange,
    onTempPeriodChange,
    pending,
    playlist,
    playlistData,
    selectedEntity,
    selectedTempEntity,
    streamsData,
    toggleExpand,
    isTopList,
    columns,
    priorityPlaylistsShown,
    setPlaylistEditing,
    canNotAnnotate,
    ...rest
  } = props;

  const customerId =
    typeof playlist.customerId === 'number'
      ? Object.keys(playlistCustomers).find(
          (key) => playlistCustomers[key] === playlist.customerId
        )
      : playlist.customerId.toLowerCase();

  const customers = useSelector((state: RootState) => state.customers.data);
  const currentCustomer = customers.filter(
    (customer: ICustomer) =>
      customer.name.toLowerCase() === customerId.toLowerCase() &&
      customer.feedType === 'streaming'
  );
  const customerName =
    (currentCustomer.length > 0 && currentCustomer[0].displayName) || '';
  const { displayName, playlistId, countryId } = playlist;
  const isExpanded =
    expandedId === playlistId && expandedCountryId === countryId;
  /** +2 because we always display DSP and PlaylistName columns, +1 - for an arrow symbol */
  const columnsCount = Object.keys(columns)?.length + 3;

  const markPriority = (event: any): void => {
    event.stopPropagation();
    if (playlist?.playlistType === 'priority') return;
    setPlaylistEditing(
      playlist?.priority
        ? null
        : { name: playlist?.displayName, id: playlist?.playlistId }
    );
  };

  let priorityType = '';
  let tooltipContent = `Is this a noteworthy playlist? 
  Click to change to a Priority Playlist for all users.`;
  if (playlist?.playlistType === 'priority') {
    if (playlist?.activity === 'current') {
      priorityType = 'disc';
      tooltipContent = 'This is a Priority Playlist (according to WMG staff)';
    }
    if (playlist?.activity === 'requested') {
      priorityType = 'rhombus';
      tooltipContent = `Priority status for this playlist has been requested.
      When it is set as Priority it will turn blue.`;
    }
  }

  return (
    <>
      <TableRow
        isTopList={isTopList}
        {...rest}
        onClick={toggleExpand(playlistId, displayName, countryId)}
        data-gtag="playlist-item"
      >
        <TableCell>
          <Box display="flex" flexDirection="column" alignItems="center">
            <CustomerIcon showHiddenPlaceholder customer={customerId} />
            <DSPEntityMeta>{customerName}</DSPEntityMeta>
          </Box>
        </TableCell>
        <TableCell paddingLeft={isTopList} width="100%">
          <Box display="flex">
            {priorityPlaylistsShown && (
              <Box
                display={canNotAnnotate || (!playlist?.activity && playlist?.playlistType === 'priority') ? 'none' : 'flex'}
                justifyContent="center"
                minWidth="60px"
                paddingTop="3px"
              >
                <TippyToolTip
                  tooltipContent={tooltipContent}
                  maxWidth="330px"
                  enabled
                >
                  <span
                    className={
                      playlist?.playlistType === 'priority'
                        ? priorityType
                        : 'bordered-plus'
                    }
                    onClick={!canNotAnnotate ? markPriority : noop}
                  >
                    {playlist?.playlistType === 'non_priority' && '+'}
                  </span>
                </TippyToolTip>
              </Box>
            )}
            <EntityMetaData>
              <EntityTitle>{playlist.displayName}</EntityTitle>
              {playlist.ownerDisplayName && (
                <EntityMeta>Owned by {playlist.ownerDisplayName}</EntityMeta>
              )}
            </EntityMetaData>
          </Box>
        </TableCell>
        {isTopList ? (
          <>
            <TableCell data-cell-value={playlist.newTrackCount}>
              <CellContent withoutPadding>
                {formatNumber(playlist.newTrackCount || 0, 'number', true)}
              </CellContent>
            </TableCell>
            <TableCell data-cell-value={playlist.totalTrackCount}>
              <CellContent withoutPadding>
                {formatNumber(playlist.totalTrackCount || 0, 'number', true)}
              </CellContent>
            </TableCell>
          </>
        ) : (
          <>
            {columns &&
              columns.length > 0 &&
              columns.map((header: ITableHeader) => {
                const isStreamsInProgressColumn =
                  header.value === 'stream_wow_in_progress';
                const hasTerritoryColumn = header?.value === 'countryId';

                if (hasTerritoryColumn)
                  return (
                    <TableCell
                      textAlign="center"
                      key={`playlist-cell${header.key}`}
                    >
                      <CountryFlag
                        small
                        id={playlist?.playlistTerritory?.id ?? 'WW'}
                        displayName={playlist?.playlistTerritory?.displayName}
                        flagImgSmall={
                          playlist?.playlistTerritory?.flagImgSmall || ''
                        }
                        flagImgMedium={
                          playlist?.playlistTerritory?.flagImgMedium || ''
                        }
                        flagImgLarge={
                          playlist?.playlistTerritory?.flagImgLarge || ''
                        }
                      />
                    </TableCell>
                  );

                return (
                  <TableCell
                    key={`playlist-cell${header.key}`}
                    data-cell-value={playlist[header.value]}
                  >
                    <CellContent
                      trend={isStreamsInProgressColumn}
                      isPositive={
                        isStreamsInProgressColumn && playlist[header.value] > 0
                      }
                    >
                      {formatNumber(
                        playlist[header.value],
                        getFormatMetric(header.value),
                        true
                      )}
                    </CellContent>
                  </TableCell>
                );
              })}
            <TableCell data-cell-value="arrow">
              <StyledCaret open={isExpanded} />
            </TableCell>
          </>
        )}
      </TableRow>

      {isExpanded && (
        <ExpandedTableRow>
          <TableCell colSpan={columnsCount}>
            <CellContent>
              <PlaylistExpandedRow
                allPeriodKeys={allPeriodKeys}
                color={color}
                customerDisplayName={displayName}
                entityId={entityId}
                fulfilled={fulfilled}
                hasNoData={hasNoData}
                isSmallScreen={isSmallScreen}
                onPeriodChange={onPeriodChange}
                onTempPeriodChange={onTempPeriodChange}
                pending={pending}
                playlist={playlist}
                playlistData={playlistData}
                selectedEntity={selectedEntity}
                selectedTempEntity={selectedTempEntity}
                streamsData={streamsData}
              />
            </CellContent>
          </TableCell>
        </ExpandedTableRow>
      )}
    </>
  );
};

export default PlaylistTableRow;
